import React, { useEffect, useState, useMemo, useCallback, Suspense } from 'react';
import { showGlobalToast } from '../utils/toast';
import { getMenuByRestaurant, createMenuItem, updateMenuItem, deleteMenuItem, updateMenuItemAvailability } from '../api/menuApi';
import { Search, Plus } from 'lucide-react';
import './MenuManagement.module.css';
import outOfStockIcon from '../assets/out-of-stock.png';
import DesktopMenuManagement from "./RestaurantMenuManagement/DesktopMenuManagement";
import MobileMenuManagement from "./RestaurantMenuManagement/MobileMenuManagement";

const AddMenuItemModal = React.lazy(() => import('./AddMenuItemModal'));

interface MenuItem {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  cuisineType?: string[];
  shortDescription?: string;
  description?: string;
  dishTags?: string[];
  spiceLevel?: string | number;
  price: number;
  discountPrice?: number;
  taxesApplicable?: boolean;
  servingSize?: string;
  availableDays?: string[];
  availableFrom?: string;
  availableTo?: string;
  keyIngredients?: string[];
  allergens?: string[];
  imageUrl?: string;
  isDefaultImage?: boolean;
  isAvailable?: boolean;
  defaultEstimationTimeInMinutes?: number;
}

// Simple debounce utility to delay function calls
const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeout: NodeJS.Timeout;
  return function (...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const MenuManagement: React.FC = () => {
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editItem, setEditItem] = useState<MenuItem | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; idToDelete: string | null }>({ show: false, idToDelete: null });
  const [search, setSearch] = useState('');
  // Removed sorting state (handled by DataGrid internally)
  // const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  // const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>([]);
  // const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  // const [showSubcategoryFilter, setShowSubcategoryFilter] = useState(false);
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = React.useState<boolean>(false);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize(); // Check on mount
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchChange = useCallback(debounce((value: string) => {
    setSearch(value);
  }, 300), []);

  useEffect(() => {
    fetchMenu();
  }, []);

  async function fetchMenu() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams(window.location.search);
      const restaurantGuid = params.get('restaurantGuid') || localStorage.getItem('restaurantGuid') || '';
      if (!restaurantGuid) throw new Error('Restaurant not found');
      const data = await getMenuByRestaurant(restaurantGuid);
      setMenu(data);
    } catch {
      setError('Failed to load menu');
    } finally {
      setLoading(false);
    }
  }

  const openAddModal = () => {
    setModalMode('add');
    setEditItem(null);
    setShowModal(true);
  };

  const openEditModal = (item: MenuItem) => {
    setModalMode('edit');
    setEditItem(item);
    setShowModal(true);
  };

  const handleDelete = (id: string) => setDeleteConfirm({ show: true, idToDelete: id });

  const confirmDeleteYes = async () => {
    if (!deleteConfirm.idToDelete) return;
    try {
      await deleteMenuItem(deleteConfirm.idToDelete);
      fetchMenu();
      showGlobalToast('Menu item deleted successfully', 'success');
    } catch {
      showGlobalToast('Failed to delete item', 'error');
    } finally {
      setDeleteConfirm({ show: false, idToDelete: null });
    }
  };

  const confirmDeleteNo = () => setDeleteConfirm({ show: false, idToDelete: null });

  const normalizeText = (txt: any) => (txt ?? '').toString().toLowerCase();

  const handleOutOfStock = async (item: MenuItem) => {
    if (loadingIds.has(item.id)) return; // prevent double clicks
    setLoadingIds(prev => new Set(prev).add(item.id));

    try {
      const res = await updateMenuItemAvailability(item.id, !item.isAvailable);

      if (res?.success) {
        // ✅ Update UI optimistically
        setMenu(prevItems =>
          prevItems.map(i =>
            i.id === item.id ? { ...i, isAvailable: !i.isAvailable } : i
          )
        );

        showGlobalToast(`Item "${item.name}" Marked as ${item.isAvailable ? 'Out of Stock' : 'Available'} successfully!`, 'success');
      } else {
        showGlobalToast(res.data?.message || 'Failed to update item availability.', 'error');
      }
    } catch (err) {
      console.error('Update error:', err);
      showGlobalToast('Something went wrong while updating availability.', 'error');
    } finally {
      // 🔄 remove from loading set
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  // Sorting state
  // eslint-disable-next-line
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' | null }>({ key: '', direction: null });

  // Helper to get normalized value for sorting
  const getSortValue = (item: MenuItem, key: string) => {
    const value = (item as any)[key];
    if (value == null) return '';
    if (key === 'price' || key === 'discountPrice') return Number(value);
    if (key === 'isAvailable') return value ? 1 : 0;
    return value.toString().toLowerCase();
  };

  const filteredMenu = useMemo(() => {
    let filtered = menu.filter(item => {
      const matchesSearch = Object.values(item).some(val => normalizeText(val).includes(normalizeText(search)));
      return matchesSearch;
    });
    if (sortConfig.key && sortConfig.direction) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, sortConfig.key);
        const bValue = getSortValue(b, sortConfig.key);
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [menu, search, sortConfig]);

  return (
    <div className="w-full mx-auto px-2 sm:px-4 md:px-6 lg:px-0 py-4 bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100 min-h-full overflow-auto">
      {/* Add/Edit Modal */}
      {showModal && (
        <Suspense fallback={<div>Loading modal...</div>}>
          <AddMenuItemModal
            open={showModal}
            isEdit={modalMode === 'edit'}
            initialValues={
              modalMode === 'edit' && editItem
                ? {
                  ...editItem,
                  spiceLevel:
                    typeof editItem.spiceLevel === 'string'
                      ? Number(editItem.spiceLevel)
                      : editItem.spiceLevel,
                }
                : undefined
            }
            onSave={async (data, addAnother) => {
              try {
                const params = new URLSearchParams(window.location.search);
                const restaurantGuid = params.get('restaurantGuid') || localStorage.getItem('restaurantGuid') || '';
                if (!restaurantGuid) throw new Error('Restaurant not found');

                const payload = {
                  ...data,
                  availableDays: Array.isArray(data.availableDays)
                    ? data.availableDays.map(String)
                    : [],
                  restaurantGuid,
                  itemImage: data.images && data.images[0] ? data.images[0] : undefined,
                  imageUrl: editItem?.isDefaultImage ? '' : data.imageUrl || '',
                  id: modalMode === 'edit' && editItem ? editItem.id : '',
                };

                if (modalMode === 'edit' && editItem) {
                  await updateMenuItem(editItem.id, payload);
                  showGlobalToast('Menu item updated successfully', 'success');
                } else {
                  await createMenuItem(payload);
                  showGlobalToast('Menu item added successfully', 'success');
                }

                if (!addAnother) setShowModal(false);
                fetchMenu();
              } catch {
                showGlobalToast('Failed to save menu item', 'error');
              }
            }}
            onCancel={() => setShowModal(false)}
          />
        </Suspense>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-2xl max-w-sm text-center transform transition-all scale-100">
            <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">Confirm Deletion</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Are you sure you want to delete this item permanently? This action cannot be undone.</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={confirmDeleteNo}
                className="px-6 py-2 rounded-xl bg-gray-200 text-gray-800 font-medium hover:bg-gray-300 transition transform hover:scale-105 active:scale-95"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteYes}
                className="px-6 py-2 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition transform hover:scale-105 active:scale-95"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      <h2 className="text-3xl font-bold mb-6">Restaurant Menu</h2>
      <div className="flex flex-col gap-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-2 mb-4 w-full">
          <div className="relative w-full md:w-auto flex-1">
            <Search style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#fb8c00', width: 20, height: 20 }} />
            <input
              type="text"
              maxLength={24}
              placeholder="Search menu items..."
              onChange={e => handleSearchChange(e.target.value)}
              className="pl-10 pr-4 py-2 border border-orange-400 rounded-full w-full md:w-64 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-orange-500"
            />
          </div>
          <button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full px-6 py-2 flex items-center gap-2 shadow transition mt-2 md:mt-0" onClick={openAddModal}>
            <Plus size={20} /> Add Item
          </button>
        </div>
        {isMobile ? (
          <MobileMenuManagement filteredMenu={filteredMenu}
            loading={loading}
            openEditModal={openEditModal}
            handleDelete={handleDelete}
            handleOutOfStock={handleOutOfStock}
            loadingIds={loadingIds}
            outOfStockIcon={outOfStockIcon} />
        ) : (
          <DesktopMenuManagement filteredMenu={filteredMenu}
            loading={loading}
            error={error}
            openEditModal={openEditModal}
            handleDelete={handleDelete}
            handleOutOfStock={handleOutOfStock}
            loadingIds={loadingIds}
            outOfStockIcon={outOfStockIcon} />
        )}
        {error && <div className="text-center text-red-500 dark:text-red-400 mt-4">{error}</div>}
      </div>
    </div>
  );
};

export default MenuManagement;